import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Eye, EyeOff, KeyRound } from 'lucide-react'
import api from '../../services/api'
import './MyProfile.css'

const formatWhen = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const MyProfile = () => {
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [show, setShow] = useState({ current: false, next: false, confirm: false })
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' })

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get('/auth/profile')
        setProfile(response.data?.data || null)
      } catch (error) {
        toast.error(error.response?.data?.message || 'Could not load your profile')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const update = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }))
  }

  const handlePassword = async (event) => {
    event.preventDefault()
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      toast.error('Enter your current password, a new password, and the confirmation')
      return
    }
    if (form.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters')
      return
    }
    if (form.newPassword !== form.confirmPassword) {
      toast.error('New password and confirmation do not match')
      return
    }
    if (form.newPassword === form.currentPassword) {
      toast.error('Choose a password that is different from the current one')
      return
    }
    try {
      setSaving(true)
      const response = await api.put('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      })
      toast.success(response.data?.message || 'Password updated')
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not change the password')
    } finally {
      setSaving(false)
    }
  }

  const initial = profile?.name?.charAt(0)?.toUpperCase() || 'U'
  const roles = (profile?.roles || []).map((role) => role.name).filter(Boolean)

  return (
    <div className="sales-dashboard profile-page">
      <div className="page-header">
        <div className="page-title">
          <h1>My profile</h1>
          <p>Your sign-in details, and a place to change your password.</p>
        </div>
      </div>

      {loading ? (
        <div className="profile-empty">Loading your profile...</div>
      ) : (
        <div className="profile-layout">
          <section className="profile-card profile-identity">
            <div className="profile-avatar">{initial}</div>
            <h2>{profile?.name || 'User'}</h2>
            <p className="profile-email">{profile?.userEmail || profile?.email || '—'}</p>
            {profile?.userCode && <span className="profile-code">{profile.userCode}</span>}
            <dl className="profile-facts">
              <div>
                <dt>Company</dt>
                <dd>{profile?.company?.name || '—'}</dd>
              </div>
              <div>
                <dt>Role</dt>
                <dd>{roles.length ? roles.join(', ') : '—'}</dd>
              </div>
              <div>
                <dt>Department</dt>
                <dd>{profile?.departmentName || '—'}</dd>
              </div>
              <div>
                <dt>Designation</dt>
                <dd>{profile?.designation || '—'}</dd>
              </div>
              <div>
                <dt>Last sign-in</dt>
                <dd>{formatWhen(profile?.lastLoggedIn)}</dd>
              </div>
            </dl>
          </section>

          <section className="profile-card">
            <div className="profile-card-head">
              <span className="profile-key"><KeyRound size={16} /></span>
              <div>
                <h2>Change password</h2>
                <p>Use your current password. The new one must be at least 6 characters.</p>
              </div>
            </div>
            <form className="profile-form" onSubmit={handlePassword}>
              {[
                ['currentPassword', 'Current password', 'current'],
                ['newPassword', 'New password', 'next'],
                ['confirmPassword', 'Confirm new password', 'confirm'],
              ].map(([field, label, key]) => (
                <label key={field}>
                  {label}
                  <span className="profile-password">
                    <input
                      type={show[key] ? 'text' : 'password'}
                      value={form[field]}
                      onChange={update(field)}
                      autoComplete={field === 'currentPassword' ? 'current-password' : 'new-password'}
                    />
                    <button
                      type="button"
                      aria-label={show[key] ? 'Hide password' : 'Show password'}
                      onClick={() => setShow((prev) => ({ ...prev, [key]: !prev[key] }))}
                    >
                      {show[key] ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </span>
                </label>
              ))}
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Updating...' : 'Update password'}
              </button>
            </form>
          </section>
        </div>
      )}
    </div>
  )
}

export default MyProfile
